<template>
  <div class="animated fadeIn" id="incPaymentsRoot">
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-header">
            <i class="fa fa-align-justify"></i> Cash Flow Plan
          </div>
          <div class="card-block">
            <div class="row">
              <div class="col-md-3 form-group">
                <label class="col-form-label pt-0">Forecasted invoice date from</label>
                <div class="col-md-2">
                  <datepicker input-class="contractual-payments-date-picker" :disabled-dates="{from: new Date(incomingPaymentFilter.filterDateTo)}" v-model="incomingPaymentFilter.filterDateFrom" format="yyyy-MM-dd"></datepicker>
                </div>
                <label class="col-form-label pt-0">to</label>
                <div class="col-md-2">
                  <datepicker input-class="contractual-payments-date-picker" :disabled="!incomingPaymentFilter.filterDateFrom" :disabled-dates="{to: new Date(incomingPaymentFilter.filterDateFrom)}" v-model="incomingPaymentFilter.filterDateTo" format="yyyy-MM-dd"></datepicker>
                </div>
              </div>
              <div class="col-md-2 form-group">
                <label class="col-form-label pt-0">Plan Type</label>
                <b-form-select class="col-md-12" v-model="incomingPaymentFilter.planType">
                  <b-form-select-option value="revenue">Revenue</b-form-select-option>
                  <b-form-select-option value="cashflow">Cash Flow</b-form-select-option>
                </b-form-select>
                <small class="form-text text-muted">Table is ordered by <span v-if="incomingPaymentFilter.planType === 'revenue'">FID / Inv. Date</span><span v-else>FPD / Inv. Due date</span> field</small>
              </div>
            </div>
            <div class="table-view"  :style="{height: invoicePaymentTableHeight}">
              <table id="cash-flow-plan" ref="invoicePaymentTable" class="table table-bordered table-striped table-condensed">
                <thead>
                  <tr class="table-data">
                    <th class="text-center table-head-th">#</th>
                    <th :class="incomingPaymentFilter.planType === 'revenue' ? 'marked-column' : null"  class="text-center table-head-th" data-toggle="tooltip" data-placement="top" title="Forecasted invoice date or invoice date">
                      FID / Inv. Date
                    </th>
                    <th :class="incomingPaymentFilter.planType === 'cashflow' ? 'marked-column' : null" class="text-center table-head-th" data-toggle="tooltip" data-placement="top" title="Forecasted payment date or invoice due date">
                      FPD / Inv. Due date
                    </th>
                    <th class="text-center table-head-th">Client</th>
                    <th class="text-center table-head-th">Project</th>
                    <th :class="incomingPaymentFilter.planType === 'revenue' ? 'marked-column' : null"  class="text-right table-head-th currency-header">Revenue</th>
                    <th class="text-right table-head-th currency-header">Prepayment</th>
                    <th :class="incomingPaymentFilter.planType === 'cashflow' ? 'marked-column' : null"  class="text-right table-head-th currency-header" data-toggle="tooltip" data-placement="top" title="To be paid or invoice paid">
                      <span v-if="incomingPaymentFilter.planType === 'cashflow'">To Be Paid / Paid + VAT</span>
                      <span v-else>To Be Paid / Paid</span>

                    </th>
                    <th class="text-center table-head-th">Description</th>
                    <th class="text-center table-head-th">Type</th>
                    <th class="text-center table-head-th">In/Out</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(payment, key) in payments" v-bind:key="key" v-if="showIncomingPayment(payment)" class="table-data">
                    <td class="text-center">
                      <span v-if="payment.type !== 'invoice' && payment.type !== 'paid'">{{ payment.id }}</span>
                      <span v-else @click="$router.push({name: 'EditContract', params: {id: payment.contract_id}});" class="action">
                        {{payment.contract_number}}
                      </span>
                    </td>
                    <td class="text-center text-nowrap">{{payment.forecasted_invoice_date }}</td>
                    <td class="text-center text-nowrap">{{payment.expected_on}}</td>
                    <td class="text-center">
                      <span v-if="payment.client" @click="$router.push({ name: 'EditClient', params: { id: payment.client.id }});" class="action">
                        {{payment.client.name}}
                      </span>
                      <span v-else>No client set</span>
                    </td>
                    <td class="text-left">
                      <span v-if="projects[payment.project_id]" @click="$router.push({ name: 'ProjectTabInvoicesPayments', params: { id: payment.project_id }});" class="action">
                        {{projects[payment.project_id].name}}
                      </span>
                      <span v-else>
                        No project set
                      </span>
                    </td>
                    <!-- <td class="currency">{{parseFloat(incomingPayment.amount).toFixed(2)}}</td> -->
                    <td class="currency text-nowrap text-monospace">{{payment.amount | numFormat('0,0.00')}}</td>
                    <td class="currency text-nowrap text-monospace">{{payment.prepayment | numFormat('0,0.00')}}</td>
                    <td class="currency text-nowrap text-monospace">
                      <span v-if="incomingPaymentFilter.planType === 'revenue'">{{payment.toBePaid | numFormat('0,0.00')}}</span>
                      <span v-else>{{payment.toBePaid_with_vat | numFormat('0,0.00')}}</span>
                    </td>
                    <td class="description text-left">{{payment.description}}</td>
                    <td class="text-center">{{payment.type}}</td>
                    <td class="text-center">
                      <span v-if="payment.incoming" class="badge badge-success">Incoming</span>
                      <span v-else class="badge badge-danger">Outgoing</span>
                    </td>
                  </tr>
                  <tr class="table-data total-sum-row">
                    <td colspan="5">Total</td>
                    <td class="d-none"></td>
                    <td class="d-none"></td>
                    <td class="d-none"></td>
                    <td class="d-none"></td>
                    <td class="currency text-nowrap text-right text-monospace">{{parseFloat(incomingPaymentsTableSum).toFixed(2) | numFormat('0,0.00')}}</td>
                    <td class="currency text-nowrap text-right text-monospace">{{parseFloat(prepaymentTableSum).toFixed(2) | numFormat('0,0.00')}}</td>
                    <td class="currency text-nowrap text-right text-monospace">{{parseFloat(toBePaidTableSum).toFixed(2) | numFormat('0,0.00')}}</td>
                    <td colspan="4"></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <b-button @click="this.exportHTMLDomToCSV">Download CSV</b-button>
          </div>
        </div>
      </div><!--/.col-->
    </div><!--/.row-->
  </div>
</template>
<style lang="scss" scoped>
  .w-100{
    width: 100%;
  }
  .description{
    word-break: break-all;
    padding-inline: 5px;
    padding-left: 0.5rem;
    font-size: 11px;
  }
  .currency::after{
    content: "€";
  }
  .currency, .currency-header {
    padding: 0 0.5rem;
  }
  .action {
    margin-left: 5px;

    &:hover {
      cursor: pointer;
      color: #ee0044;
    }
  }
  .badge-warning{
    &:hover {
      cursor: pointer;
      background-color: #ee0044;
    }
  }

  table {
    font-size: 12px;
  }

  tr.table-data td:not(:first-child):not(:last-child),
  tr.table-data th:not(:first-child):not(:last-child) {
    text-align: right;
  }
  th, td, td > select {
    padding: 0.15rem;
    vertical-align: middle;
  }
  tfoot tr * {
    height: 25px !important;
    margin-bottom: 0;
    margin-top: 0;
    vertical-align: top;
    resize: none;
  }
  tbody > tr:hover {
    background-color: #c8d2d2;
  }
  .total-sum-row {
    font-weight: bold;
  }
  input[type="date"] {
    padding: 0;
  }

  .table-head-th {
    /*min-width: 80px;*/
    position: sticky;
    position: -webkit-sticky;
    top: 0;
    z-index: 1;
    background: white;
  }
  .table-view {
    overflow-y: scroll;
    position: relative;
    max-height: 800px;
  }

  .contractual-payments-date-picker {
    width: 100%;
    border: 1px solid rgba(0, 0, 0, 0.15);
    height: 1.8125rem;
    color: #607d8b;
    padding: 0.5rem 0.75rem;
  }
  .contractual-payments-table-date-picker {
    width: 100%;
    border: 1px solid rgba(0, 0, 0, 0.15);
    color: #607d8b;
    padding-left: 10px;
  }
  .edit-contractual-payments-date-picker {
    width: 100%;
    border: 1px solid rgba(0, 0, 0, 0.15);
    color: #607d8b;
    padding: 0.5rem 0.75rem;
  }
  .vdp-datepicker__calendar {
    min-width: 200px;
    max-width: fit-content;
  }
  label.vue-switcher {
    margin-bottom: 0;
  }

  .marked-column {
    background-color: #cacaca;
  }
</style>
<style>
  table .edit-contractual-payments-date-picker {
    width: 80px;
  }
</style>
<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import modal from 'vue-strap/src/Modal';
import VueNotifications from 'vue-notifications';
import moment from 'moment';
import Datepicker from 'vuejs-datepicker';
import Switches from 'vue-switches';

export default {
  name: 'IncomingPayments',
  components: {
    modal,
    Datepicker,
    Switches,
  },
  data: function () {
    return {
      payments: [],
      invoicePaymentTableHeight: 'auto',
      incomingPaymentFilter: {
        filterDateFrom: moment().startOf('month').format('YYYY-MM-DD'),
        filterDateTo: moment().endOf('month').format('YYYY-MM-DD'),
        planType: 'revenue'
      },
      incomingPaymentsTableSum: '',
      prepaymentTableSum: '',
      toBePaidTableSum: '',
      hasLoaded: {
        invoices: false,
        incomingPayments: false
      }
    };
  },
  props: {
    projectId: '',
  },
  watch: {
    invoices: {
      handler: function (invoices) {
        const invoicesArray = [];
        for (const invoiceID in invoices) {
          const invoice = invoices[invoiceID];

          // if invoice has payment then dont show the payment in table
          if (invoice.payment) {
            const plannedPaymentID = this.payments.find((el) => el.id === invoice.payment.id);

            if (typeof plannedPaymentID !== 'undefined') {
              this.$delete(this.payments, plannedPaymentID);
            }
          }

          const invoiceTemplate = {
            client: this.getClientByProjectID(invoice.project_id) ?? {},
            amount: invoice.revenue / 100,
            description: invoice.contract.contract_name,
            expected_on: invoice.due_date,
            forecasted_invoice_date: invoice.date,
            id: `${invoice.invoice_id}_inv`,
            contract_id: invoice.contract.id,
            contract_number: invoice.contract.contract_number,
            incoming: 1,
            prepayment: invoice.prepayment / 100,
            project_id: invoice.project_id,
            toBePaid: invoice.sum / 100,
            toBePaid_with_vat: invoice.sum_with_vat / 100,
            type: 'invoice'
          };

          if (invoice.payment_date) {
            invoiceTemplate.expected_on = invoice.payment_date;
            invoiceTemplate.type = 'paid';
          }

          invoicesArray.push(invoiceTemplate);
          // }
        }

        if (!this.hasLoaded.payments) this.payments = [...this.payments, ...invoicesArray];

        this.recalculateTableSum();
      },
      deep: true
    },
    incomingPayments: {
      handler: function (val) {
        const incomingPayments = [];

        for (const paymentID in val) {
          const incomingPayment = val[paymentID];
          if (incomingPayment.type === 'planned') {
            // if project has VAT inserted
            if (this.projects[incomingPayment.project_id]) incomingPayment.toBePaid_with_vat = incomingPayment.toBePaid + (incomingPayment.toBePaid * (this.projects[incomingPayment.project_id].vat_rate / 100));
            else incomingPayment.toBePaid_with_vat = incomingPayment.toBePaid;

            incomingPayment.client = this.getClientByProjectID(incomingPayment.project_id) ?? {};

            incomingPayments.push(incomingPayment);
          }
        }

        if (!this.hasLoaded.incomingPayments) this.payments = [...this.payments, ...incomingPayments];

        this.hasLoaded.incomingPayments = true;
        this.recalculateTableSum();
      },
      deep: true
    },
    incomingPaymentFilter: {
      handler: function (val) {
        this.recalculateTableSum();
      },
      deep: true
    },
  },
  computed: {
    ...mapGetters('IncomingPayments', [
      'incomingPayments',
    ]),
    ...mapGetters('JiraProjects', [
      'projects',
    ]),
    ...mapGetters('Clients', [
      'invoices', 'invoicesByDueDate', 'clients'
    ]),
    newIncomingPaymentToBePaid: function () {
      const toBePayd = parseFloat(this.newIncomingPaymentAmount) + parseFloat(this.newIncomingPaymentPrepayment);
      return toBePayd ? toBePayd.toFixed(2) : 0;
    },
  },
  methods: {
    ...mapActions('IncomingPayments', [
      'LOAD_INCOMING_PAYMENTS',
    ]),
    ...mapActions('Clients', [
      'GET_INVOICES_LIST',
      'GET_CLIENTS_LIST',
    ]),
    showIncomingPayment(incomingPayment) {
      let filterDateField = 'forecasted_invoice_date';

      if (this.incomingPaymentFilter.planType === 'cashflow') filterDateField = 'expected_on';

      if (this.incomingPaymentFilter.filterDateFrom != ''
              || this.incomingPaymentFilter.filterDateTo /* &&
                  incomingPayment[filterDateField] !== '' */
      ) {
        return (
          !this.projectId
                      && incomingPayment[filterDateField] >= moment(this.incomingPaymentFilter.filterDateFrom).format('YYYY-MM-DD')
                      && incomingPayment[filterDateField] <= moment(this.incomingPaymentFilter.filterDateTo).format('YYYY-MM-DD')
        )
                  || (
                    incomingPayment.project_id == this.projectId
                      && incomingPayment[filterDateField] >= moment(this.incomingPaymentFilter.filterDateFrom).format('YYYY-MM-DD')
                      && incomingPayment[filterDateField] <= moment(this.incomingPaymentFilter.filterDateTo).format('YYYY-MM-DD')
                  );
      }
      return !this.projectId && this.projectId == incomingPayment.project_id;
    },
    recalculateTableSum() {
      let sum = 0;
      let sum2 = 0;
      let sum3 = 0;

      this.payments.map((payment) => {
        if (this.showIncomingPayment(payment) && payment.incoming) {
          sum += payment.amount;
          sum2 += payment.prepayment;
          if (this.incomingPaymentFilter.planType === 'cashflow') {
            if (payment.toBePaid_with_vat) sum3 += payment.toBePaid_with_vat;
          } else if (payment.toBePaid) sum3 += payment.toBePaid;
        }
      });

      this.incomingPaymentsTableSum = sum;
      this.prepaymentTableSum = sum2;
      this.toBePaidTableSum = sum3;

      this.sortPayments();
    },
    sortPayments() {
      let filterDateField = 'forecasted_invoice_date';

      if (this.incomingPaymentFilter.planType === 'cashflow') filterDateField = 'expected_on';

      this.payments.sort((a, b) => {
        if (!a[filterDateField]) return 1;
        if (!b[filterDateField]) return -1;
        return moment.utc(a[filterDateField]).diff(moment.utc(b[filterDateField]));
      });
    },
    getClientByProjectID(project_id) {
      return this.clients.find((client) => {
        if (this.projects[project_id]) return client.id === this.projects[project_id].client_id;
        return false;
      });
    },
    exportHTMLDomToCSV() { // not good not bad
      const filename = 'cash-flow-plan.csv';
      const csv = [];
      const rows = this.$el.querySelectorAll('table#cash-flow-plan tr');

      for (let i = 0; i < rows.length; i++) {
        const row = [];
        const cols = rows[i].querySelectorAll('td, th');

        for (let j = 0; j < cols.length; j++) {
          row.push(`"${cols[j].innerText}"`);
        }

        csv.push(row.join(','));
      }

      const csvFile = new Blob([csv.join('\n')], { type: 'text/csv' });
      const downloadLink = document.createElement('a');
      downloadLink.download = filename;
      downloadLink.href = window.URL.createObjectURL(csvFile);
      downloadLink.style.display = 'none';
      document.body.appendChild(downloadLink);
      downloadLink.click();
    },
  },
  notifications: {
    showSuccessMsg: {
      type: VueNotifications.types.success
    },
    showInfoMsg: {
      type: VueNotifications.types.info
    },
    showWarnMsg: {
      type: VueNotifications.types.warn
    },
    showErrorMsg: {
      type: VueNotifications.types.error
    }
  },
  mounted() {
    this.GET_CLIENTS_LIST();
    this.LOAD_INCOMING_PAYMENTS();
    this.GET_INVOICES_LIST();
  }
};
</script>
